/**
 * Лента сообщений комнаты.
 *
 * Задача модуля — отдать UI плоский список «строк», уже сгруппированных по
 * автору, и уметь догружать историю вверх. Всё, что знает про внутренности
 * SDK (EventTimeline, paginateEventTimeline, статусы отправки), живёт здесь.
 */
import {
  Direction,
  EventStatus,
  EventType,
  MsgType,
  type MatrixClient,
  type MatrixEvent,
  type Room,
  type Thread,
} from "matrix-js-sdk";
import { getLangSnapshot, tf, tr } from "../i18n";

import { replyTargetId, stripFormattedReplyFallback, stripReplyFallback } from "./relations";
import { displayBody, displayFormattedBody } from "./mentions";
import { senderLabel } from "./rooms";
import { withRateLimit } from "./roomActions";

export interface TimelineItem {
  eventId: string;
  sender: string;
  /** MXID автора — нужен для проверки «своё сообщение» в контекстном меню. */
  senderId: string;
  senderName: string;
  ts: number;
  body: string;
  /** Sanitized-at-render-time Matrix HTML, or null for plain messages. */
  formattedBody: string | null;
  /** Сообщение — действие («/me»), рисуется иначе. */
  emote: boolean;
  /** Локальное эхо ещё не подтверждено сервером. */
  pending: boolean;
  /** Отправка провалилась — показываем ошибку и даём повторить. */
  failed: boolean;
  /** true — событие отредактировано (показываем пометку). */
  edited: boolean;
  /**
   * НАДГРОБИЕ: сообщение удалено (m.room.redaction), но остаётся в ленте
   * пустым пузырём «Message was deleted» — как в Element и Telegram
   * (постановка Антона 2026-09-21 09:29: «оно просто исчезает из ленты»).
   *
   * ПОЧЕМУ СТРОКА НЕ ЛЕЖИТ В `body`. Текст надгробия подставляет UI через
   * `useT()`, а не этот модуль. buildTimeline вызывается на событиях
   * таймлайна, а не на смене языка: положи мы сюда готовую фразу — при
   * переключении языка все надгробия остались бы на старом до следующего
   * события в комнате. Поэтому здесь только ФЛАГ, а `body` намеренно `""`.
   *
   * Побочная польза пустого body: `canCopy`, «Поделиться» и правка в меню
   * сообщения смотрят именно на `item.body` — на надгробии они гаснут сами,
   * без отдельной проверки в каждой ветке.
   *
   * ЧТО ОСТАЁТСЯ У УДАЛЁННОГО СОБЫТИЯ (замер на matrix-js-sdk 42.3.0,
   * 2026-09-21): `getType()`, `getSender()`, `getTs()`, `getId()` живы;
   * `getContent()` пуст `{}`; `getRelation()` — null; `threadRootId` —
   * undefined. То есть отправитель и время для надгробия ЕСТЬ, а тело,
   * вложение, цитата и связь с тредом — нет, и взяться им неоткуда.
   */
  redacted: boolean;
  /**
   * true — это сообщение упоминает ТЕКУЩЕГО пользователя (подсветка в ленте
   * и счётчик в списке комнат). Источники — поле `m.mentions` (MSC3952):
   * либо `user_ids` содержит мой id, либо `room: true` (@room).
   */
  mentionsMe: boolean;
  /** true — первое сообщение в группе одного автора: рисуем имя и аватар. */
  startsGroup: boolean;
  /**
   * Вложение, если сообщение — файл/картинка. null — обычный текст.
   *
   * Здесь именно mxc://, А НЕ готовый http: наш Synapse отдаёт media только
   * по авторизованному эндпойнту (замер в media.ts), и прямая ссылка в <img>
   * даёт битую картинку. Превращение в blob: делает UI через useAuthedMedia.
   */
  attachment: {
    /** msgtype события: m.image / m.video / m.audio / m.file — по нему UI
        решает, ЧЕМ открывать вложение (img, video, audio или карточка
        файла) и в ленте, и в лайтбоксе. */
    msgtype: string;
    mxc: string | null;
    /** Original filename used for download and file-type detection. */
    name: string;
    /** Optional visible caption, separate from the original filename. */
    caption: string | null;
    size: number;
    /** MIME из info.mimetype — по нему рисуем иконку типа файла и
        собираем File для Web Share API. */
    mimetype: string;
  } | null;
  /**
   * Это сообщение — ответ на другое (m.in_reply_to). Цитата для рендера;
   * null — обычное сообщение. Если оригинал не загружен в таймлайн,
   * found=false и UI показывает «сообщение не загружено» без падения.
   */
  replyTo: {
    eventId: string;
    found: boolean;
    senderName: string;
    snippet: string;
    /** mxc миниатюры для цитаты-картинки/видео (Антон 2026-09-14). null — текст. */
    thumbMxc: string | null;
    /** Имя файла для подписи под миниатюрой. */
    thumbName: string | null;
  } | null;
  /**
   * id корня треда, если это сообщение — ответ в треде (MSC3440).
   * null — обычное сообщение в главной ленте.
   */
  threadRootId: string | null;
  /**
   * Индикатор треда под сообщением в главной ленте, или null, если к
   * сообщению треда нет (или клиент о нём ещё не знает — тред, который
   * не подгружен, индикатора не получит; это безопасная сторона ошибки:
   * пропустить индикатор допустимо, нарисовать ложный — нет).
   *
   *  - count — число ответов (thread.length по SDK);
   *  - lastSenderName — имя автора ПОСЛЕДНЕГО ответа: одной строкой видно
   *    и «тред живой», и кто его поддерживает, без открытия панели;
   *  - unread — есть ли в треде ответы, которые текущий пользователь ещё
   *    не читал (по тредовым receipts, см. rooms.ts/unreadOf).
   */
  thread: { count: number; lastSenderName: string; unread: boolean } | null;
  /**
   * Упоминания из события (MSC3952 m.mentions). Нужны, чтобы @Имя в тексте
   * могло стать кликабельной ссылкой в DM (Антон, 2026-09-14).
   * displayName — то, как человек подписан в комнате на момент чтения.
   * self=true — упоминание СЕБЯ: рендерится подсвеченным текстом, НЕ ссылкой
   * на DM с самим собой (Антон 2026-09-14), но «Имя:» → «@Имя» применяется
   * так же, как для чужих (Антон 2026-10-09).
   */
  mentions: { userId: string; displayName: string; self?: boolean }[] | null;
  /**
   * Системная строка (приглашение, вход, выход, смена настроек) — не null,
   * если это НЕ сообщение, а служебное событие. Рисуется по центру,
   * приглушённым текстом, без аватара и пузыря (Антон, патч 2026-09-10 10:34).
   *
   * `kind` — ключ схлопывания: подряд идущие однотипные строки объединяются
   * («и ещё 5 участников вошли»), иначе массовое приглашение забило бы ленту.
   * kind'ы намеренно мелкие («leave-self» ≠ «kick»): добровольный выход и
   * исключение НЕ схлопываем вместе — это разные вещи для модерации.
   *
   * `names` — имена участников для схлопывания (кто вошёл/вышел/приглашён).
   */
  system: { kind: string; text: string; names?: string[] } | null;

  /**
   * Карточка звонка в ленте (MSC3401 / MatrixRTC, Антон 2026-09-11 13:03:
   * «звонок должен быть элементом ЛЕНТЫ внизу, как в Slack/Element»).
   * Не null, если этот элемент — звонок, а не сообщение и не обычная
   * системная строка.
   *
   * Одна карточка на ОДИН эпизод звонка: серия state-событий
   * org.matrix.msc3401.call.member (входы, обновления, выходы-пустышки {})
   * сегментируется по непрерывности (тишина > 10 мин = новый звонок) —
   * см. callCardsOf.
   *
   *  - active  — звонок идёт прямо сейчас: UI рисует «Идёт звонок · N»
   *    и кнопку «Присоединиться» (тот же useCallJoin, что в шапке);
   *  - participants — уникальные MXID всех, кто побывал в этом звонке
   *    (устройства одного человека схлопнуты по state_key);
   *  - names — displayname'ы для подписи (на момент события);
   *  - startedTs — первый вход в серии (created_ts ?? origin_server_ts);
   *  - endedTs — последний выход в серии; null, если звонок ещё идёт
   *    (или шёл, но «протух» по expires — тогда конца в данных нет, и
   *    длительность НЕ показываем: выдуманное время хуже отсутствующего).
   */
  call: {
    active: boolean;
    participants: number;
    names: string[];
    startedTs: number;
    endedTs: number | null;
  } | null;
}

/**
 * Одна реакция в агрегированном виде: эмодзи + сколько человек её поставили
 * + моя ли она (для подсветки) + id моего события-реакции (для снятия).
 *
 * myEventId нужен, потому что снятие реакции = redact СВОЕГО события
 * m.reaction, а не «отправка минус-один». Без id мы не сможем удалить
 * именно ту реакцию, которую поставили сами.
 */
export interface ReactionChip {
  key: string;
  count: number;
  mine: boolean;
  /** id моего события-реакции (для redact при toggle-off). */
  myEventId?: string;
}

/** Порог группировки: сообщения одного автора в пределах 5 минут — одна группа. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

/**
 * Какие события мы показываем КАК СООБЩЕНИЯ.
 *
 * Осознанно узко: только m.room.message с текстовым содержимым. Системные
 * события (членство, смена настроек) идут ОТДЕЛЬНОЙ веткой — systemItemOf,
 * см. ниже. m.room.encrypted сюда не попадает никогда: crypto выключен,
 * расшифровать нечем, и плашку про шифрование рисует UI по состоянию
 * комнаты, а не по отдельным событиям.
 */
const ATTACHMENT_TYPES: string[] = [MsgType.Image, MsgType.File, MsgType.Video, MsgType.Audio];

function isRenderable(ev: MatrixEvent): boolean {
  // Реакции (m.annotation) не показываем строкой: их собирает buildReactions,
  // а здесь они давали бы пустые строки без body. Проверка ДО RoomMessage:
  // иначе сработает отсечка по типу и реакция просто не дойдёт до фильтра.
  if (ev.getType() === EventType.Reaction) return false;
  if (ev.getType() !== EventType.RoomMessage) return false;
  // Redacted сюда не доходит: его перехватывает ветка надгробия ДО вызова
  // isRenderable. Проверка остаётся страховкой — у redacted события content
  // пуст, и обычным сообщением оно быть не может ни при каких условиях.
  if (ev.isRedacted()) return false;
  // Замены (m.replace) не показываем отдельной строкой: SDK применит их
  // к исходному событию, а его мы и так рендерим.
  if (ev.getRelation()?.rel_type === "m.replace") return false;
  const content = ev.getContent();
  const msgtype = String(content.msgtype ?? "");
  // Вложения добавлены на этапе «базовые функции»: отправлять файл и не
  // показывать его в ленте было бы хуже, чем не уметь отправлять вовсе.
  if (
    msgtype !== MsgType.Text &&
    msgtype !== MsgType.Emote &&
    msgtype !== MsgType.Notice &&
    !ATTACHMENT_TYPES.includes(msgtype)
  ) {
    return false;
  }
  return typeof content.body === "string" && content.body.length > 0;
}

/**
 * НАДГРОБИЕ (tombstone): является ли событие удалённым СООБЩЕНИЕМ,
 * которое обязано остаться в ленте пустым баллоном.
 *
 * БАГ (Антон, 2026-09-21 09:29): «при удалении сообщения оно ПРОСТО
 * ИСЧЕЗАЕТ из ленты». Должно оставаться надгробие с временем и фразой
 * «Message was deleted», как в Element/Telegram. Причина была в isRenderable:
 * `if (ev.isRedacted()) return false` выбрасывал событие целиком.
 *
 * ГРАНИЦЫ — КАЖДАЯ ПОСТАВЛЕНА ЗАМЕРОМ на настоящем matrix-js-sdk 42.3.0:
 *
 *  - только `m.room.message`. `getType()` ПЕРЕЖИВАЕТ redaction (тип входит
 *    в REDACT_KEEP_KEYS), поэтому проверка по типу работает и после удаления.
 *    СНЯТАЯ РЕАКЦИЯ (redact своего `m.reaction`) и УДАЛЁННОЕ ЧЛЕНСТВО
 *    (`m.room.member`) надгробия НЕ получают — проверено замерами C и D.
 *    Снятие реакции — штатное действие, надгробие на нём было бы абсурдом;
 *  - state-события отбрасываем явно (`isState()`): сообщением они не бывают;
 *  - ЛОКАЛЬНОЕ ЭХО удаления тоже даёт надгробие, и это нужно:
 *    `markLocallyRedacted` выставляет `unsigned.redacted_because` сразу, до ответа
 *    сервера, и `isRedacted()` уже true (замер S3). Так надгробие появляется
 *    сразу по клику «Удалить», а не после круга до сервера и обратно.
 *
 * ИЗВЕСТНОЕ ОГРАНИЧЕНИЕ (замер A). Если redact'нуть САМО событие-правку
 * (`m.replace`), то после удаления `getRelation()` возвращает null: связь лежала
 * в content, а content стёрт. Отличить такую правку от удалённого сообщения
 * невозможно в принципе, и она даст лишнее надгробие. Наш UI такого не
 * создаёт: `redactMessage` удаляет `item.eventId`, а правки в items не попадают
 * вовсе (их режет фильтр `m.replace` в isRenderable). Прийти такое может только
 * от другого клиента. Цена ошибки выбрана осознанно: лишнее надгробие в редком
 * случае вредит меньше, чем исчезающие сообщения в обычном — а именно это чиним.
 */
function isTombstone(ev: MatrixEvent): boolean {
  if (!ev.isRedacted()) return false;
  if (ev.isState()) return false;
  return ev.getType() === EventType.RoomMessage;
}

/**
 * Построить TimelineItem-надгробие для удалённого сообщения.
 *
 * Сохраняем ровно то, что пережило redaction и проверено замером F:
 * `eventId`, `sender`/`senderId`, `senderName`, `ts`. Всё остальное гасим явно:
 * тело, вложение, цитата, упоминания, индикатор треда и пометка «изменено»
 * у удалённого сообщения смысла не имеют, а данных для них всё равно нет.
 *
 * `body` НАМЕРЕННО пустой — текст надгробия подставляет UI через useT(),
 * чтобы смена языка перерисовывала его сразу (см. комментарий у поля `redacted`).
 *
 * `pending`/`failed` тоже false: статус отправки САМОГО удалённого сообщения
 * больше ни на что не влияет, а прозрачность pending в ленте означает
 * «ещё не у сервера» и на надгробии читалась бы как ещё одно состояние.
 */
function tombstoneItemOf(
  ev: MatrixEvent,
  room: Room,
  ts: number,
  startsGroup: boolean,
  fallbackId: string,
): TimelineItem {
  return {
    eventId: ev.getId() ?? fallbackId,
    sender: ev.getSender() ?? "",
    senderId: ev.getSender() ?? "",
    senderName: senderLabel(room, ev),
    ts,
    // Текст надгробия — в UI, здесь пусто (см. поле `redacted`).
    body: "",
    formattedBody: null,
    attachment: null,
    emote: false,
    pending: false,
    failed: false,
    edited: false,
    mentionsMe: false,
    mentions: null,
    startsGroup,
    replyTo: null,
    threadRootId: null,
    thread: null,
    system: null,
    call: null,
    redacted: true,
  };
}

/**
 * Фильтрация сирот после redact (MSC3912=False).
 *
 * Сервер при redact сообщения НЕ удаляет связанные события (реакции, правки,
 * реплаи) — они остаются в таймлайне, ссылаясь на удалённое. Клиент обязан
 * сам их скрывать, иначе под пустым местом висит ряд чипсов или цитата
 * «сообщение не найдено».
 *
 * Правило: событие с rel_type="m.annotation" (реакция) или rel_type="m.replace"
 * (правка), чья цель (event_id в relates_to) отсутствует в ленте ИЛИ
 * isRedacted() — не рендерим.
 *
 * Реплаи (m.in_reply_to) НЕ фильтруем здесь: они рендерятся как обычные
 * сообщения, и их цитата — отдельная логика этапа 4.
 */
function isOrphanedRelation(ev: MatrixEvent, eventsById: Map<string, MatrixEvent>): boolean {
  const rel = ev.getRelation();
  if (!rel) return false;
  const relType = rel.rel_type;
  const targetId = rel.event_id;
  if (!targetId) return false;

  if (relType === "m.annotation" || relType === "m.replace") {
    const target = eventsById.get(targetId);
    if (!target || target.isRedacted()) return true;
  }
  return false;
}

/**
 * Упоминает ли context события текущего пользователя.
 *
 * `m.mentions` — MSC3952: `user_ids` (адресные) и/или `room: true` (@room).
 * Отсутствие поля или пустые user_ids = не упоминает.
 */
function mentionsUserId(content: Record<string, unknown>, me: string): boolean {
  const mentions = content["m.mentions"] as
    | { user_ids?: string[]; room?: boolean }
    | undefined;
  if (!mentions) return false;
  if (mentions.room) return true;
  return Array.isArray(mentions.user_ids) && mentions.user_ids.includes(me);
}

export function buildTimeline(room: Room, me: string): TimelineItem[] {
  const events = room.getLiveTimeline().getEvents();
  const threadRepliesByRoot = threadRepliesFromRoom(room);
  // Индекс для быстрого поиска цели связанных событий: event_id → MatrixEvent.
  const eventsById = new Map<string, MatrixEvent>();
  for (const ev of events) {
    const id = ev.getId();
    if (id) eventsById.set(id, ev);
  }

  const items: TimelineItem[] = [];

  for (const ev of events) {
    // БАГ (Антон, 2026-09-10 13:05): «сообщения уходят не в тред а в основной
    // чат». Отправка была ВЕРНА — событие уходит с rel_type="m.thread", —
    // но главная лента показывала ВСЕ события подряд, включая тредовые.
    // Выглядело так, будто ответ ушёл в общий чат: он появлялся и в панели
    // треда, и в основной ленте. Ответы в треде живут только в треде;
    // в главной ленте от них остаётся индикатор на корневом сообщении.
    // Корень треда НЕ отбрасываем: у него threadRootId=null (проверено
    // по lib/models/event.ts), он обычное сообщение комнаты.
    if (ev.threadRootId && ev.threadRootId !== ev.getId()) continue;

    // События звонка (org.matrix.msc3401.call.member) НЕ обрабатываем
    // в цикле: серия state-событий схлопывается в ОДНУ карточку звонка,
    // и делает это вставка СРАЗУ ПОСЛЕ цикла (callCardOf), а не построчный
    // обход — иначе один звонок дал бы двадцать строк «вошёл/вышел».
    if (ev.getType() === EventType.GroupCallMemberPrefix) continue;

    // Системные события — отдельная ветка ДО фильтра сообщений: у них нет
    // body/msgtype, и isRenderable их честно отбросил бы.
    const system = systemItemOf(ev, room);
    if (system) {
      const ts = ev.getTs();
      items.push({
        eventId: ev.getId() ?? `local-${ts}-${items.length}`,
        sender: ev.getSender() ?? "",
        senderId: ev.getSender() ?? "",
        senderName: senderLabel(room, ev),
        ts,
        body: "",
        formattedBody: null,
        attachment: null,
        emote: false,
        pending: false,
        failed: false,
        edited: false,
        mentionsMe: false,
        // Системная строка группы не начинает и не продолжает: шапку
        // (имя+время) для неё не рисуем, а следующее обычное сообщение
        // начнёт новую группу — см. вычисление prev ниже.
        startsGroup: false,
        replyTo: null,
        threadRootId: null,
        thread: null,
        system,
        call: null,
        mentions: null,
        redacted: false,
      });
      continue;
    }

    // НАДГРОБИЕ удалённого сообщения — строго ДО isRenderable: там стоит
    // `if (ev.isRedacted()) return false`, из-за которого удалённое сообщение
    // исчезало из ленты целиком (баг Антона 2026-09-21 09:29).
    //
    // ГРУППИРОВКА У НАДГРОБИЯ ТАКАЯ ЖЕ, КАК У СООБЩЕНИЯ, и это намеренно:
    // надгробие стоит в своём ряду ленты (входящее слева / исходящее справа),
    // и выпадение из группы ломало бы форму соседних пузырей вокруг него.
    if (isTombstone(ev)) {
      const prevItem = items[items.length - 1];
      const prev = prevItem && !prevItem.system && !prevItem.call ? prevItem : undefined;
      const ts = ev.getTs();
      const sender = ev.getSender() ?? "";
      const startsGroup = !prev || prev.sender !== sender || ts - prev.ts > GROUP_WINDOW_MS;
      items.push(tombstoneItemOf(ev, room, ts, startsGroup, `local-${ts}-${items.length}`));
      continue;
    }

    if (!isRenderable(ev)) continue;
    if (isOrphanedRelation(ev, eventsById)) continue;

    const status = ev.status;
    const sender = ev.getSender() ?? "";
    // Отредактированное событие: SDK держит замену отдельно, а актуальный
    // текст отдаёт через getContent() исходника только после applyEdit —
    // поэтому берём replacingEvent, если он есть.
    const replacing = ev.replacingEvent();
    const content = replacing ? replacing.getContent()["m.new_content"] ?? ev.getContent() : ev.getContent();
    // Цитата реплая рисуется отдельным блоком (replyTo), поэтому текстовый
    // и HTML fallback по спеке срезаем — иначе оригинал виден ДВАЖДЫ.
    const isReply = replyTargetId(ev) !== null;
    const mentions = mentionsOf(content, room, me);
    const rawFormatted = typeof content.formatted_body === "string" ? content.formatted_body : null;
    // Имя для матча «Имя:» берём из formatted_body, когда оно есть (там
    // имя НА МОМЕНТ ОТПРАВКИ), а не текущее displayName (Антон 2026-10-09).
    const body = displayBody(stripReplyFallback(String(content.body ?? ""), isReply), mentions, rawFormatted);
    const formattedBody = rawFormatted === null ? null : displayFormattedBody(stripFormattedReplyFallback(rawFormatted, isReply), mentions);

    // Системная строка между двумя сообщениями одного автора ПРЕРЫВАЕТ
    // группу: имя и время у следующего сообщения рисуем заново, иначе
    // второе сообщение визуально «приклеилось» бы к системной строке.
    // Карточка звонка прерывает группу так же.
    const prevItem = items[items.length - 1];
    const prev = prevItem && !prevItem.system && !prevItem.call ? prevItem : undefined;
    const ts = ev.getTs();
    const startsGroup =
      !prev || prev.sender !== sender || ts - prev.ts > GROUP_WINDOW_MS;

    items.push({
      eventId: ev.getId() ?? `local-${ts}-${items.length}`,
      sender,
      senderId: ev.getSender() ?? "",
      senderName: senderLabel(room, ev),
      ts,
      body,
      formattedBody,
      attachment: attachmentOf(content),
      emote: content.msgtype === MsgType.Emote,
      pending: status === EventStatus.SENDING || status === EventStatus.QUEUED,
      failed: status === EventStatus.NOT_SENT,
      edited: Boolean(replacing),
      mentionsMe: mentionsUserId(content, me),
      mentions,
      startsGroup,
      replyTo: replyOf(ev, eventsById, room),
      // Ответ в треде: SDK выставляет threadRootId по relates_to.rel_type.
      // Для корня треда isThreadRoot=true и threadRootId=null — он остаётся
      // в главной ленте и открывает тред по клику.
      threadRootId: ev.threadRootId ?? null,
      thread: threadIndicatorOf(ev, room, me, threadRepliesByRoot),
      system: null,
      call: null,
      redacted: false,
    });
  }

  // Карточки звонков — ПОСЛЕ цикла и до схлопывания системных строк.
  // Их может быть несколько (тишина > 10 мин режет историю на отдельные
  // звонки), и каждой — своё хронологическое место в ленте.
  const callCards = callCardsOf(room);
  for (const call of callCards) {
    const ts = call.endedTs ?? call.startedTs;
    items.push({
      // Синтетический id: карточка не является одним событием, а собрана
      // из серии. Префикс $ НЕ ставим: permalink/редактирование на неё
      // не должны срабатывать (см. canShareLink в Timeline).
      //
      // В id входят ОБА конца эпизода (начало и точка размещения):
      // у двух активных карточек совпадал бы `call-card-<ts>`, и React
      // получал два узла с одинаковым key (Антон 2026-09-18, п.2:
      // две карточки «Call ongoing» с одной и той же отметкой 13:01).
      eventId: `call-card-${call.startedTs}-${ts}`,
      sender: "",
      senderId: "",
      senderName: "",
      ts,
      body: "",
      formattedBody: null,
      attachment: null,
      emote: false,
      pending: false,
      failed: false,
      edited: false,
      mentionsMe: false,
      mentions: null,
      startsGroup: false,
      replyTo: null,
      threadRootId: null,
      thread: null,
      system: null,
      call,
      redacted: false,
    });
  }
  if (callCards.length > 0) {
    // Позиция карточки — её хронологическое место (у активного звонка —
    // низ ленты, у завершённого — момент окончания). Без этой сортировки
    // карточки уезжали бы в конец списка независимо от времени.
    items.sort((a, b) => a.ts - b.ts);
  }

  return collapseSystemItems(items);
}

/**
 * Собрать индикатор треда для сообщения главной ленты.
 *
 * Индикатор полагается ТОЛЬКО на объект Thread в памяти SDK: `getThread`
 * возвращает null для сообщения без ответов — и это правильно, индикатора
 * у пустого места быть не должно. Первый ответ создаёт тред на сервере,
 * SDK поднимает объект Thread, и индикатор появляется сам при следующем
 * refresh ленты (RoomEvent.Timeline).
 *
 * Имя последнего ответившего берём через senderLabel по событию lastReply:
 * thread.length сам по себе не говорит, КТО там, а индикатор без автора
 * выглядит мёртвой кнопкой «N ответов».
 *
 * unread — по тредовым receipts: у Thread в SDK 42.3.0 свой hasUserReadEvent
 * (Thread наследует ReadReceipt), и receipts с thread_id туда и складываются.
 * Это тот же критерий, что в rooms.ts/hasUnreadThreadReplies — менять его
 * в одном месте и забыть в другом нельзя, поэтому логика здесь дублирует
 * её сознательно и со ссылкой.
 */
function threadIndicatorOf(
  ev: MatrixEvent,
  room: Room,
  me: string,
  /** rootId -> ответы из таймлайна КОМНАТЫ (см. threadRepliesFromRoom). */
  fromRoom: Map<string, MatrixEvent[]>,
): TimelineItem["thread"] {
  const id = ev.getId();
  if (!id) return null;
  const thread = room.getThread(id);
  const roomReplies = fromRoom.get(id) ?? [];

  // БАГ (Антон, 2026-09-10 13:27): «в основном чате пометки о треде нет».
  // Опирались только на room.getThread(id) — а он возвращает объект лишь
  // после того, как SDK построит тред (подтверждение сервером + sync).
  // Сразу после отправки первого ответа треда ещё нет, и пометка не
  // появлялась вовсе. Поэтому второй источник: ответы с threadRootId,
  // лежащие в таймлайне комнаты (включая локальное эхо).
  if (!thread && roomReplies.length === 0) return null;

  const last = thread?.lastReply() ?? roomReplies[roomReplies.length - 1];
  const lastId = last?.getId();
  // Счётчик берём по максимуму из двух источников: у thread.length может
  // не быть свежего эха, а в комнатном таймлайне — старых ответов.
  const count = Math.max(thread?.length ?? 0, roomReplies.length);
  // Непрочитанным считаем тред, чей ПОСЛЕДНИЙ ответ не покрыт моей
  // тредовой отметкой. Свой собственный ответ непрочитанным быть не может.
  const unread = Boolean(
    thread &&
    last &&
    lastId &&
    !lastId.startsWith("~") &&
    last.getSender() !== me &&
    !thread.hasUserReadEvent(me, lastId),
  );

  return {
    count,
    lastSenderName: last ? senderLabel(room, last) : "",
    unread,
  };
}

/**
 * Ответы в тредах, найденные в таймлайне КОМНАТЫ, сгруппированные по корню.
 *
 * Нужны, потому что room.getThread() пуст до подтверждения сервером, а
 * пометка «N ответов» должна появляться сразу (см. threadIndicatorOf).
 * Считаем ОДИН раз на пересборку ленты: иначе на каждое сообщение шёл бы
 * полный проход по таймлайну — квадрат на длинной истории.
 */
function threadRepliesFromRoom(room: Room): Map<string, MatrixEvent[]> {
  const byRoot = new Map<string, MatrixEvent[]>();
  for (const ev of room.getLiveTimeline().getEvents()) {
    const root = ev.threadRootId;
    if (!root || root === ev.getId()) continue;
    if (!isRenderable(ev)) continue;
    const list = byRoot.get(root);
    if (list) list.push(ev);
    else byRoot.set(root, [ev]);
  }
  for (const list of byRoot.values()) list.sort((a, b) => a.getTs() - b.getTs());
  return byRoot;
}

/**
 * Собрать системную строку из события, или null, если событие не системное
 * (обычное сообщение, реакция, нечитаемое) либо системное, но бессодержательное
 * (повторный join без смены профиля, redacted).
 *
 * ПОКРЫТИЕ (Антон, патч 2026-09-10 10:34):
 *  - m.room.member: invite / join / leave (сам вышел | кик | разбан) / ban,
 *    смена имени и аватара участника;
 *  - m.room.name / m.room.topic / m.room.avatar: смена настроек комнаты.
 *
 * ИМЕНА. Кого касается событие (state_key у member) берём из content
 * .displayname — это имя НА МОМЕНТ события, а не нынешнее; нынешнее при
 * рендере старой истории было бы анахронизмом. Кто совершил действие
 * (sender) — через senderLabel, как у обычных сообщений.
 */
function systemItemOf(ev: MatrixEvent, room: Room): TimelineItem["system"] {
  const type = ev.getType();
  if (type !== EventType.RoomMember &&
      type !== EventType.RoomName &&
      type !== EventType.RoomTopic &&
      type !== EventType.RoomAvatar) {
    return null;
  }
  if (ev.isRedacted()) return null;

  const content = ev.getContent();
  const prev = ev.getPrevContent() as Record<string, unknown>;
  const actor = senderLabel(room, ev);

  if (type === EventType.RoomName) {
    const name = String(content.name ?? "");
    if (name === String(prev.name ?? "")) return null;
    return {
      kind: "room-name",
      text: name
        ? tf("timeline.roomNameChanged", { actor, name })
        : tf("timeline.roomNameRemoved", { actor }),
    };
  }
  if (type === EventType.RoomTopic) {
    const topic = String(content.topic ?? "");
    if (topic === String(prev.topic ?? "")) return null;
    return {
      kind: "room-topic",
      text: topic
        ? tf("timeline.roomTopicChanged", { actor })
        : tf("timeline.roomTopicRemoved", { actor }),
    };
  }
  if (type === EventType.RoomAvatar) {
    const url = String(content.url ?? "");
    if (url === String(prev.url ?? "")) return null;
    return {
      kind: "room-avatar",
      text: url
        ? tf("timeline.roomAvatarChanged", { actor })
        : tf("timeline.roomAvatarRemoved", { actor }),
    };
  }

  // m.room.member. Кого касается — state_key; имя — из content.displayname,
  // запасной вариант — сам MXID.
  const subject = ev.getStateKey() ?? "";
  if (!subject) return null;
  const newDisplay =
    typeof content.displayname === "string" ? content.displayname : "";
  const oldDisplay =
    typeof prev.displayname === "string" ? prev.displayname : "";
  const subjectName = newDisplay || oldDisplay || subject;

  const membership = String(content.membership ?? "");
  const prevMembership = String(prev.membership ?? "");
  const reason =
    typeof content.reason === "string" && content.reason ? ` (${content.reason})` : "";

  switch (membership) {
    case "invite":
      // Приглашение видно СРАЗУ, до всякого ответа приглашённого — это и
      // есть смысл строки. Отдельная строка про вход появится позже (join).
      if (prevMembership === "invite") return null; // повторный инвайт — не спамим
      return {
        kind: "invite",
        text: tf("timeline.memberInvited", { actor, subject: subjectName }),
        names: [subjectName],
      };
    case "join": {
      if (prevMembership === "join") {
        // Уже был в комнате: это не вход, а смена профиля.
        if (newDisplay !== oldDisplay) {
          return {
            kind: "profile-name",
            text: tf("timeline.memberRenamed", {
              oldName: oldDisplay || subject,
              newName: newDisplay || subject,
            }),
          };
        }
        if (String(content.avatar_url ?? "") !== String(prev.avatar_url ?? "")) {
          return {
            kind: "profile-avatar",
            text: tf("timeline.memberAvatarChanged", { subject: subjectName }),
          };
        }
        return null; // join→join без изменений (переподтверждение) — не показываем
      }
      // Вход: из invite (принял приглашение), из leave (вернулся), с нуля
      // (публичная комната). Это ОТДЕЛЬНАЯ строка, не слитая с invite:
      // между ними может пройти сколько угодно времени (Антон, 10:34).
      return {
        kind: "join",
        text: tf("timeline.memberJoined", { subject: subjectName }),
        names: [subjectName],
      };
    }
    case "leave": {
      if (prevMembership === "leave") return null;
      // Разбан: unban на сервере — это membership leave от ЧУЖОГО sender'а
      // при предыдущем ban. Показываем, иначе «разбанил, а тишина».
      if (prevMembership === "ban" && ev.getSender() !== subject) {
        return {
          kind: "unban",
          text: tf("timeline.memberUnbanned", { actor, subject: subjectName }),
          names: [subjectName],
        };
      }
      if (ev.getSender() === subject) {
        return {
          kind: "leave-self",
          text: tf("timeline.memberLeft", { subject: subjectName }),
          names: [subjectName],
        };
      }
      // Кик: sender ≠ state_key, причина (reason) видна модераторам.
      return {
        kind: "kick",
        text: tf("timeline.memberKicked", { actor, subject: subjectName, reason }),
        names: [subjectName],
      };
    }
    case "ban":
      return {
        kind: "ban",
        text: tf("timeline.memberBanned", { actor, subject: subjectName, reason }),
        names: [subjectName],
      };
    default:
      return null;
  }
}

/** Родительные формы для схлопнутых строк: «A, B и ещё 3 участника …». */
const COLLAPSED_TEXT: Record<string, (names: string) => string> = {
  join: (n) => tf("timeline.summaryJoined", { names: n }),
  invite: (n) => tf("timeline.summaryInvited", { names: n }),
  "leave-self": (n) => tf("timeline.summaryLeft", { names: n }),
  kick: (n) => tf("timeline.summaryKicked", { names: n }),
  ban: (n) => tf("timeline.summaryBanned", { names: n }),
  unban: (n) => tf("timeline.summaryUnbanned", { names: n }),
};

/**
 * Схлопнуть подряд идущие однотипные системные строки в одну.
 *
 * Зачем: массовое приглашение (админ зовёт 20 человек) иначе выдало бы 20
 * одинаковых строк и забило ленту (Антон, патч 10:34). Схлопываем ТОЛЬКО
 * kind'ы из COLLAPSED_TEXT, у которых есть имена: «X сменил тему» дважды
 * подряд не схлопываем — там терялся бы сам факт второй смены.
 *
 * Формат: до трёх имён перечисляем, дальше «A, B и ещё N участников».
 * Склонение «участников» упрощено до неизменной формы — точное склонение
 * по числительным здесь не стоит своей функции.
 */
function collapseSystemItems(items: TimelineItem[]): TimelineItem[] {
  const out: TimelineItem[] = [];
  for (const item of items) {
    const sys = item.system;
    const prev = out[out.length - 1];
    if (
      sys &&
      sys.names &&
      COLLAPSED_TEXT[sys.kind] &&
      prev?.system &&
      prev.system.kind === sys.kind &&
      prev.system.names
    ) {
      const names = [...prev.system.names, ...sys.names];
      prev.system = {
        kind: sys.kind,
        names,
        text: COLLAPSED_TEXT[sys.kind](formatNameList(names)),
      };
      continue;
    }
    out.push(item);
  }
  return out;
}

/**
 * Форма ключа «и ещё N участников» по числу. В русском три формы
 * (1 участник, 2 участника, 5 участников), в остальных языках — две,
 * поэтому выбор формы живёт в коде, а слова — в словаре.
 * Образец — `membersWord` в `src/ui/RoomPage.tsx`.
 */
function namesMoreKey(
  n: number,
): "timeline.namesMoreOne" | "timeline.namesMoreFew" | "timeline.namesMoreMany" {
  if (getLangSnapshot() !== "ru") {
    return n === 1 ? "timeline.namesMoreOne" : "timeline.namesMoreMany";
  }
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "timeline.namesMoreMany";
  const mod10 = n % 10;
  if (mod10 === 1) return "timeline.namesMoreOne";
  if (mod10 >= 2 && mod10 <= 4) return "timeline.namesMoreFew";
  return "timeline.namesMoreMany";
}

/** «A», «A и B», «A, B и C», «A, B и ещё 4 участника». */
function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2)
    return tf("timeline.namesTwo", {
      first: names[0] ?? "",
      second: names[1] ?? "",
    });
  if (names.length === 3)
    return tf("timeline.namesThree", {
      first: names[0] ?? "",
      second: names[1] ?? "",
      third: names[2] ?? "",
    });
  const count = names.length - 2;
  return tf(namesMoreKey(count), {
    first: names[0] ?? "",
    second: names[1] ?? "",
    count,
  });
}

/* ===================== Карточка звонка (MSC3401) =====================
 *
 * Постановка Антона 2026-09-11 13:03 (критичная): плашка «Присоединиться»
 * НАД лентой на планшете уезжает из поля зрения — звонок должен быть
 * элементом ЛЕНТЫ внизу, как в Slack/Element. И после завершения в
 * истории обязана остаться запись «был звонок, участники, длительность».
 *
 * ИСТОЧНИК — те же state-события org.matrix.msc3401.call.member, что
 * читает MatrixRTCSession (rtc.ts). Они УЖЕ лежат в живом таймлайне;
 * раньше лента их просто отбрасывала (isRenderable пускал только
 * m.room.message).
 *
 * ФОРМАТ СОБЫТИЯ (проверен по базе стенда и по SDK session.ts):
 *  - content с полями application/call_id/device_id/foci_preferred —
 *    живое членство (JOIN или keep-alive UPDATE; у UPDATE есть created_ts);
 *  - пустой content {} — ЯВНЫЙ ВЫХОД (12:56:46 JOIN → 12:57:42 {} LEAVE);
 *  - если человек просто закрыл вкладку, {} не придёт ВООБЩЕ, и членство
 *    протухает по expires (дельта мс от created_ts ?? origin_server_ts,
 *    дефолт 4 часа — DEFAULT_EXPIRE_DURATION в CallMembership.ts).
 *
 * ГРУППИРОВКА. Событий на один звонок много: каждый вход, каждый
 * keep-alive, каждый выход — отдельное state-событие (state_key = MXID
 * участника). Человеку нужна ОДНА карточка на звонок, поэтому события
 * сегментируются по НЕПРЕРЫВНОСТИ: пока интервал занят хотя бы одним
 * живым членством (или тишина между событиями меньше
 * CALL_SEGMENT_GAP_MS), это один звонок. Тишина длиннее порога —
 * граница: «звонок утром» и «звонок вечером» обязаны остаться ДВУМЯ
 * записями в истории, иначе хронология лжёт. Один звонок в комнате в
 * один момент — дефолтный слот (call_id "" / "ROOM"), и стенд других
 * не показывает, поэтому разносить по call_id здесь сознательно НЕ
 * делаем.
 *
 * ЗАВЕРШЕНИЕ. Сегмент считается завершённым, когда:
 *  (а) у КАЖДОГО участника сегмента есть явный выход {} — endedTs = ts
 *      последнего выхода, длительность показываем;
 *  (б) живых членств не осталось и настенные часы ушли за expires —
 *      звонок протух. Конца в данных НЕТ: endedTs=null и длительность
 *      НЕ показываем (выдуманное время хуже отсутствующего — спека).
 *
 * ПЕРЕСЧЁТ ПО НАСТЕННЫМ ЧАСАМ (ветка б): UI обязан пересобирать ленту
 * по таймеру — событие протухания само по себе в таймлайн не приходит.
 */

/** Дефолт срока жизни членства — тот же, что в CallMembership.ts SDK. */
const CALL_MEMBERSHIP_DEFAULT_EXPIRES_MS = 4 * 60 * 60 * 1000;

/**
 * Порог тишины между событиями членства, после которого это уже НОВЫЙ
 * звонок. 10 минут: keep-alive пишется раз в несколько минут (SDK
 * обновляет членство до истечения expires), а человеческий «повторный
 * звонок» после 10 минут тишины в истории — отдельное событие, а не
 * продолжение. Порог человеческий, не протокольный: протоколу всё равно,
 * он про «сейчас», а не про историю.
 */
const CALL_SEGMENT_GAP_MS = 10 * 60 * 1000;

/** Парсированная выжимка одного события m.call.member. */
interface CallMemberSlice {
  /** MXID участника — state_key события. */
  userId: string;
  /** displayname на момент события (из комнаты), запасной вариант — MXID. */
  name: string;
  /** Начало членства: created_ts ?? origin_server_ts события. */
  startTs: number;
  /** Абсолютный момент протухания членства, мс (startTs + expires). */
  expiryTs: number;
  /** origin_server_ts события — момент ЯВНОГО выхода для content {}. */
  evTs: number;
  /** true — content непустой (живое членство); false — {} (выход). */
  live: boolean;
}

/**
 * Собрать карточки звонков комнаты. Пустой массив — звонков в
 * загруженной истории не было вовсе (ни одного события m.call.member).
 * Именно карточКИ: тишина длиннее CALL_SEGMENT_GAP_MS режет историю на
 * отдельные звонки, и у каждого своя запись в ленте.
 */
function callCardsOf(room: Room, now = Date.now()): NonNullable<TimelineItem["call"]>[] {
  const events = room.getLiveTimeline().getEvents();
  const slices: CallMemberSlice[] = [];

  for (const ev of events) {
    if (ev.getType() !== EventType.GroupCallMemberPrefix) continue;
    if (ev.isRedacted()) continue;
    // КТО ЭТО — БЕРЁМ ИЗ SENDER, А НЕ ИЗ STATE_KEY.
    //
    // Дефект Антона 12.09 10:36 («техническая информация о звонке всё ещё
    // не убрана»): в карточке вместо имён стояло
    //   _@ab:algsuccess.com_ALGW3DB085F2B4C8_m.call
    // Причина: у MatrixRTC (MSC3401/MSC4143) state_key НЕ равен MXID — он
    // device-scoped и склеен из подчёркиваний: _<mxid>_<deviceId>_<app>.
    // room.getMember() по такой строке не находит никого, и запасной путь
    // подставлял её саму как «имя».
    //
    // sender у события членства — всегда сам участник, обычный MXID,
    // поэтому имя по нему находится. state_key используем только если он
    // и правда выглядит как MXID (старый формат, где ключ = пользователь).
    const rawKey = ev.getStateKey() ?? "";
    const userId = rawKey.startsWith("@") ? rawKey : (ev.getSender() ?? "");
    if (!userId) continue;
    const content = ev.getContent() as Record<string, unknown>;
    const evTs = ev.getTs();
    // {} = явный выход. Всё остальное (даже неполное) считаем живым
    // членством: строгая валидация — работа MatrixRTCSession, а здесь
    // лента истории, где важнее не потерять факт звонка, чем отбросить
    // кривое событие.
    const live = Object.keys(content).length > 0;
    const startTs =
      typeof content.created_ts === "number" ? content.created_ts : evTs;
    const expires =
      typeof content.expires === "number"
        ? content.expires
        : CALL_MEMBERSHIP_DEFAULT_EXPIRES_MS;
    slices.push({
      userId,
      // Если имени нет (человек вышел из комнаты, профиль не подгружен) —
      // показываем ЛОКАЛЬНУЮ ЧАСТЬ mxid, а не весь технический идентификатор:
      // «ab» вместо «@ab:algsuccess.com». Полный mxid в ленте — тот же шум.
      name: room.getMember(userId)?.name ?? localpartOf(userId),
      startTs,
      expiryTs: startTs + expires,
      evTs,
      live,
    });
  }

  if (slices.length === 0) return [];

  // Сегментация по времени событий: сортируем и режем на куски, где
  // между соседними событиями тишина больше порога. Внутри куска живые
  // членства могут пересекаться и не пересекаться — для карточки важно,
  // что это один эпизод разговора, а не два разных.
  //
  // ДУБЛЬ КАРТОЧКИ (Антон, 2026-09-18, п.2 постановки). Одной паузы мало:
  // keep-alive у MatrixRTC пишется редко (expires по умолчанию 4 часа), и
  // в обычном разговоре между «вошёл» и «вышел» легко проходит больше
  // 10 минут ТИШИНЫ. Тогда событие выхода {} отрезалось в НОВЫЙ сегмент:
  //   • старый сегмент оставался без выхода вовсе — по нему карточка
  //     считалась АКТИВНОЙ («идёт звонок») до самого протухания членства;
  //   • новый сегмент из одного {} давал ВТОРУЮ карточку «звонок
  //     завершён» с тем же самым разговором.
  // Ровно это человек и видит как дубль после выхода участника.
  //
  // Поэтому граница сегмента теперь требует ДВУХ условий сразу:
  //   (1) тишина больше порога И
  //   (2) предыдущий сегмент к этому моменту ПУСТ — ни одного живого
  //       непротухшего членства.
  // Критерий «живого» тот же, что у useCallPresence и у callCardOfSegment
  // (last event живой И expiryTs > момента) — иначе лента и плашка снова
  // разошлись бы в оценке «звонок идёт».
  slices.sort((a, b) => a.evTs - b.evTs);
  const segments: CallMemberSlice[][] = [[slices[0]]];
  /** Открытые членства ТЕКУЩЕГО сегмента: userId -> момент протухания. */
  const open = new Map<string, number>();
  const applyToOpen = (s: CallMemberSlice) => {
    if (s.live) open.set(s.userId, s.expiryTs);
    else open.delete(s.userId);
  };
  applyToOpen(slices[0]);
  for (let i = 1; i < slices.length; i++) {
    const s = slices[i];
    const gap = s.evTs - slices[i - 1].evTs;
    // Занят ли сегмент на момент этого события хоть кем-то живым.
    let occupied = false;
    for (const expiryTs of open.values()) {
      if (expiryTs > s.evTs) {
        occupied = true;
        break;
      }
    }
    // Явный выход участника ВСЕГДА принадлежит тому сегменту, где он
    // вошёл: закрывающее событие не может открыть новый звонок.
    const closesOpenMembership = !s.live && open.has(s.userId);
    if (gap > CALL_SEGMENT_GAP_MS && !occupied && !closesOpenMembership) {
      segments.push([]);
      open.clear();
    }
    segments[segments.length - 1].push(s);
    applyToOpen(s);
  }

  return segments.map((seg) => callCardOfSegment(seg, now));
}

/**
 * Локальная часть MXID для запасной подписи: «@ab:algsuccess.com» -> «ab».
 * Сервер в ленте не нужен: комната одна, домен у всех один и тот же.
 */
function localpartOf(userId: string): string {
  if (!userId.startsWith("@")) return userId;
  const colon = userId.indexOf(":");
  return colon > 1 ? userId.slice(1, colon) : userId.slice(1);
}

/** Собрать одну карточку из одного сегмента событий членства. */
function callCardOfSegment(
  seg: CallMemberSlice[],
  now: number,
): NonNullable<TimelineItem["call"]> {
  // Последнее событие по каждому участнику: для карточки важно, КТО
  // побывал в звонке и жив ли он сейчас, а не вся история keep-alive.
  const lastByUser = new Map<string, CallMemberSlice>();
  for (const s of seg) {
    const prev = lastByUser.get(s.userId);
    if (!prev || s.evTs > prev.evTs) lastByUser.set(s.userId, s);
  }
  const lastSlices = [...lastByUser.values()];

  // Активен сейчас: у кого-то последнее событие живое И ещё не протухло.
  // Ровно тот же критерий, что у useCallPresence (isExpired у SDK), —
  // расхождение дало бы «плашка есть, карточки нет».
  const liveSlices = lastSlices.filter((s) => s.live && s.expiryTs > now);
  const active = liveSlices.length > 0;

  // СОСТАВ ОДНОЙ КАРТОЧКИ ОБНОВЛЯЕТСЯ (Антон 2026-09-18, п.2:
  // ±«одна карточка, обновляющая состав»). Пока звонок ИДЁТ, карточка
  // показывает тех, кто в нём СЕЙЧАС: иначе счётчик только растёт и после
  // выходов показывает больше людей, чем реально разговаривает («7 и 5
  // участников» на скрине). ЗАВЕРШЁННАЯ карточка — запись в истории,
  // там правильно показать всех, кто в звонке побывал.
  const shown = active ? liveSlices : lastSlices;

  const names: string[] = [];
  const seenNames = new Set<string>();
  for (const s of [...shown].sort((a, b) => a.startTs - b.startTs)) {
    if (seenNames.has(s.name)) continue;
    seenNames.add(s.name);
    names.push(s.name);
  }

  // Начало звонка — самое раннее начало членства (created_ts ?? ts).
  const startedTs = Math.min(...seg.map((s) => s.startTs));

  // Конец: ТОЛЬКО по явным выходам. Если у каждого участника сегмента
  // последнее событие — выход {}, звонок завершён в момент последнего
  // из них. Иначе (кто-то протух по expires, а не по {}) конца в данных
  // нет — endedTs=null, длительность не показываем.
  let endedTs: number | null = null;
  if (!active && lastSlices.every((s) => !s.live)) {
    endedTs = Math.max(...lastSlices.map((s) => s.evTs));
  }

  return {
    active,
    participants: shown.length,
    names,
    startedTs,
    endedTs,
  };
}

/**
 * Извлечь данные реплая из события.
 *
 * Возвращает null, если событие не является ответом. Если является —
 * возвращает объект с found=false, когда оригинал не загружен в текущий
 * таймлайн (старая история, которую ещё не подтянули). UI в таком случае
 * показывает «сообщение не загружено» и не ломается.
 *
 * БАГ (Антон, 2026-09-21 10:11): «цитаты в ответах не работают ВООБЩЕ».
 * Здесь стояло `ev.getRelation()?.["m.in_reply_to"]`, а `getRelation()`
 * в SDK 42.3.0 отдаёт null всему, у чего нет `rel_type` на верхнем уровне
 * `m.relates_to` — то есть КАЖДОМУ обычному rich reply. Функция выходила
 * на первой строке всегда, и цитата не строилась ни разу. Чтение связи
 * переехало в `relations.ts` (`replyTargetId`): wire content + clear
 * content, плюс отсев тредового фолбэка `is_falling_back:true`.
 */
function replyOf(
  ev: MatrixEvent,
  eventsById: Map<string, MatrixEvent>,
  room: Room,
): TimelineItem["replyTo"] {
  const replyToId = replyTargetId(ev);
  if (!replyToId) return null;

  const original = eventsById.get(replyToId);
  if (!original || original.isRedacted()) {
    return {
      eventId: replyToId,
      found: false,
      senderName: "",
      snippet: original?.isRedacted() ? tr("message.deleted") : "",
      thumbMxc: null,
      thumbName: null,
    };
  }

  // Если ЦИТИРУЕМОЕ сообщение само было ответом, в его body лежит своя
  // fallback-цитата. Без среза цитата на реплай показывала бы склейку
  // «> <@кто-то> позапрошлый текст прошлый текст» (замер probe-nested, $c).
  const originalBody = stripReplyFallback(
    String(original.getContent().body ?? ""),
    replyTargetId(original) !== null,
  );
  const snippet = originalBody.replace(/\s+/g, " ").slice(0, 100);
  // Миниатюра в цитате (Антон, 2026-09-14 10:00): если оригинал — картинка
  // или видео, передаём её mxc, чтобы UI показал thumbnail 32-40px.
  // Для файлов/аудио миниатюры нет — остаётся текст.
  const originalAtt = attachmentOf(original.getContent() as Record<string, unknown>);
  const thumbMxc =
    originalAtt && (originalAtt.msgtype === "m.image" || originalAtt.msgtype === "m.video")
      ? originalAtt.mxc
      : null;
  return {
    eventId: replyToId,
    found: true,
    senderName: senderLabel(room, original),
    snippet,
    thumbMxc,
    thumbName: originalAtt?.name ?? null,
  };
}

/**
 * Вытащить вложение из content события.
 *
 * Отдаём СЫРОЙ mxc://: превращение в загружаемый адрес требует авторизации
 * и живёт в media.ts — здесь чистые данные события, без сетевых подробностей.
 *
 * Зашифрованные вложения (поле `file` вместо `url`) СОЗНАТЕЛЬНО не поддержаны:
 * crypto в клиенте выключен, расшифровать нечем.
 */
export function attachmentOf(content: Record<string, unknown>): TimelineItem["attachment"] {
  const msgtype = String(content.msgtype ?? "");
  if (!ATTACHMENT_TYPES.includes(msgtype)) return null;
  const info = (content.info ?? {}) as { size?: number; mimetype?: string };
  const body = String(content.body ?? "");
  const filename = typeof content.filename === "string" && content.filename.length > 0
    ? content.filename
    : body || tr("timeline.attachmentFallback");
  return {
    msgtype,
    mxc: typeof content.url === "string" ? content.url : null,
    name: filename,
    caption: body && body !== filename ? body : null,
    size: Number(info.size ?? 0),
    mimetype: String(info.mimetype ?? ""),
  };
}

/**
 * Догрузить страницу истории вверх.
 *
 * @returns true, если что-то ещё осталось (можно грузить дальше).
 *
 * Используем paginateEventTimeline на live-таймлайне: в MemoryStore нет
 * кеша, поэтому каждая страница — реальный /messages к серверу. Возврат
 * false означает «сервер сказал, что начало комнаты достигнуто».
 */
export async function loadOlder(client: MatrixClient, room: Room, limit = 40): Promise<boolean> {
  const timeline = room.getLiveTimeline();
  // /messages режется rc_message так же, как отправка: при быстрой прокрутке
  // большой комнаты без ретрая получаем 429 и «История не догрузилась».
  return await withRateLimit(() =>
    client.paginateEventTimeline(timeline, {
      backwards: true,
      limit,
    }),
  );
}

/** Есть ли ещё история вверх (по мнению SDK), без сетевого запроса. */
export function canLoadOlder(room: Room): boolean {
  return Boolean(room.getLiveTimeline().getPaginationToken(Direction.Backward));
}

// --- Треды (этап 5, MSC3440) -------------------------------------------------

/**
 * Собрать ленту ответов треда.
 *
 * Источник — thread.liveTimeline.getEvents(): в SDK 42.3.0 туда попадают
 * ТОЛЬКО события с rel_type="m.thread" (проверено по lib/models/thread.ts:
 * фильтр в конструкторе TimelineSet). Корень треда здесь НЕ живёт — он
 * остаётся в room.getLiveTimeline() с isThreadRoot=true.
 *
 * Системные события (m.room.member и пр.) в треде не показываем: они не
 * имеют rel_type="m.thread" и сюда не попадают. Если сервер однажды начнёт
 * класть их в тред — будет отдельная правка, здесь осознанно упрощаем.
 */
export function buildThreadTimeline(
  room: Room,
  rootId: string,
  thread: Thread | null,
  me: string,
): TimelineItem[] {
  // БАГ (Антон, 2026-09-10 13:21): «сообщение в тред нигде не появляется».
  // Раньше лента треда читалась ТОЛЬКО из thread.liveTimeline. Но пока
  // тред не создан (первый ответ) объекта треда нет вовсе, а локальное эхо
  // отправленного события SDK кладёт в таймлайн КОМНАТЫ. Плюс главная
  // лента теперь отбрасывает всё с threadRootId — в итоге ответ исчезал
  // из обоих мест: в треде его ещё нет, в комнате уже не показываем.
  // Поэтому собираем ОБЪЕДИНЕНИЕ: события треда + события комнаты,
  // относящиеся к этому корню. Дубли снимаем по event_id, порядок по времени.
  const seen = new Set<string>();
  const events: MatrixEvent[] = [];
  const add = (ev: MatrixEvent) => {
    const id = ev.getId();
    // У неподтверждённого эха id уже есть (txn-id), но после подтверждения
    // сервер выдаёт другой — SDK сам заменяет событие, поэтому по id
    // достаточно.
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    events.push(ev);
  };
  for (const ev of thread?.liveTimeline.getEvents() ?? []) add(ev);
  for (const ev of room.getLiveTimeline().getEvents()) {
    if (ev.threadRootId === rootId) add(ev);
  }
  events.sort((a, b) => a.getTs() - b.getTs());

  const eventsById = new Map<string, MatrixEvent>();
  for (const ev of events) {
    const id = ev.getId();
    if (id) eventsById.set(id, ev);
  }

  const items: TimelineItem[] = [];
  for (const ev of events) {
    // Надгробие и в ленте треда: удалённый ответ обязан оставаться видным
    // точно так же, как в главной. В треде каждый ответ — самостоятельная строка
    // (startsGroup=true всегда), поэтому группировку не считаем.
    //
    // ОГОВОРКА (замер E): SDK при redact тредового ответа САМ переносит его
    // в главную ленту (`makeRedacted` → `moveAllRelatedToMainTimeline`) и обнуляет
    // `threadRootId`. То есть на практике надгробие удалённого тредового ответа
    // окажется в ГЛАВНОЙ ленте, а не здесь. Это поведение SDK (MSC3389
    // не принят), а не наш выбор; ветка здесь нужна для случаев, когда redacted
    // событие пришло уже удалённым с сервера и осталось в тредовом таймлайне.
    if (isTombstone(ev)) {
      const ts = ev.getTs();
      items.push(tombstoneItemOf(ev, room, ts, true, `local-${ts}-${items.length}`));
      continue;
    }
    if (!isRenderable(ev)) continue;
    if (isOrphanedRelation(ev, eventsById)) continue;

    const status = ev.status;
    const sender = ev.getSender() ?? "";
    const replacing = ev.replacingEvent();
    const content = replacing ? replacing.getContent()["m.new_content"] ?? ev.getContent() : ev.getContent();
    const ts = ev.getTs();
    // То же, что в главной ленте: fallback-цитата не должна дублировать
    // блок replyTo. В треде реплаи встречаются чаще, чем в комнате.
    const isReply = replyTargetId(ev) !== null;
    const mentions = mentionsOf(content, room, me);
    const rawFormatted = typeof content.formatted_body === "string" ? content.formatted_body : null;
    const body = displayBody(stripReplyFallback(String(content.body ?? ""), isReply), mentions, rawFormatted);
    const formattedBody = rawFormatted === null ? null : displayFormattedBody(stripFormattedReplyFallback(rawFormatted, isReply), mentions);

    items.push({
      eventId: ev.getId() ?? `local-${ts}-${items.length}`,
      sender,
      senderId: ev.getSender() ?? "",
      senderName: senderLabel(room, ev),
      ts,
      body,
      formattedBody,
      attachment: attachmentOf(content),
      emote: content.msgtype === MsgType.Emote,
      pending: status === EventStatus.SENDING || status === EventStatus.QUEUED,
      failed: status === EventStatus.NOT_SENT,
      edited: Boolean(replacing),
      mentionsMe: mentionsUserId(content, me),
      mentions,
      // В треде каждый ответ — самостоятельная строка: группировка по автору
      // визуально склеивает диалог в один пузырь, и теряется смысл треда.
      startsGroup: true,
      replyTo: replyOf(ev, eventsById, room),
      threadRootId: ev.threadRootId ?? null,
      // Индикатора треда ВНУТРИ треда не бывает: мы уже в нём.
      thread: null,
      system: null,
      call: null,
      redacted: false,
    });
  }
  return items;
}

/**
 * Упоминания из события: список {userId, displayName, self?} для кликабельных
 * @Имя в тексте. displayName — то, как человек подписан в комнате на
 * момент чтения (не MXID).
 *
 * Самоупоминание (userId === me) НЕ фильтруем (Антон 2026-10-09): иначе
 * сообщение «ab: начинаю переключение?», адресованное мне, получало
 * mentions=null и «Имя:» не превращалось в «@Имя». Вместо фильтрации помечаем
 * его self=true — UI рисует его подсвеченным текстом без ссылки на DM
 * с самим собой (правило Антона 2026-09-14 сохраняется).
 *
 * @returns null, если упоминаний нет вовсе.
 */
function mentionsOf(
  content: Record<string, unknown>,
  room: Room,
  me: string,
): TimelineItem["mentions"] {
  const raw = content["m.mentions"] as { user_ids?: string[] } | undefined;
  const userIds = raw?.user_ids;
  if (!userIds || userIds.length === 0) return null;
  return userIds.map((userId) => ({
    userId,
    displayName: room.getMember(userId)?.name ?? userId,
    self: userId === me || undefined,
  }));
}

/**
 * Догрузить страницу истории треда вверх.
 *
 * @returns true, если что-то ещё осталось (можно грузить дальше).
 */
export async function loadThreadOlder(
  client: MatrixClient,
  thread: Thread,
  limit = 40,
): Promise<boolean> {
  return await withRateLimit(() =>
    client.paginateEventTimeline(thread.liveTimeline, {
      backwards: true,
      limit,
    }),
  );
}

/** Есть ли ещё история вверх в треде (по мнению SDK), без сетевого запроса. */
export function canLoadThreadOlder(thread: Thread): boolean {
  return Boolean(thread.liveTimeline.getPaginationToken(Direction.Backward));
}

/**
 * Собрать реакции комнаты в агрегированном виде.
 *
 * ПОЧЕМУ ВРУЧНУЮ, А НЕ SDK: RelationsHelper в 42.3.0 экспериментальный,
 * и поведение при догрузке истории (старые реакции приходят пачкой) у него
 * неочевидно. Ручной обход надёжнее и прозрачнее.
 *
 * ПРАВИЛА:
 * 1. Реакция — событие m.reaction с rel_type="m.annotation" и key (эмодзи).
 * 2. Одна реакция одного человека на одно сообщение — одна запись. Дубли
 *    (два одинаковых эмодзи от одного отправителя) объединяем: последний
 *    по времени перекрывает предыдущий (спека не запрещает, но дважды
 *    считать один голос — плохая идея).
 * 3. Сортировка: по убыванию count, при равенстве — по времени первого
 *    появления (стабильно). Это чтобы чипсы не прыгали под курсором при
 *    добавлении новой реакции с тем же счётом.
 * 4. Удалённые реакции (redact) не попадают: их нет в eventsById, или они
 *    isRedacted. Это обеспечивает isOrphanedRelation выше + фильтр здесь.
 */
export function buildReactions(
  room: Room,
  me: string,
): Map<string, ReactionChip[]> {
  const events = room.getLiveTimeline().getEvents();
  return aggregateReactions(events, me);
}

/**
 * Собрать реакции, относящиеся к событиям ТРЕДА.
 *
 * ЗАЧЕМ ОТДЕЛЬНАЯ ФУНКЦИЯ (дефект первого захода по тредам): m.reaction
 * НЕ несёт rel_type="m.thread" — реакция на ответ треда лежит в ГЛАВНОМ
 * таймлайне комнаты и ссылается (m.annotation) на event_id ответа. Поэтому
 * buildReactions(room, …) эти реакции ФОРМАЛЬНО видит, но ОТБРАСЫВАЕТ:
 * цели (тредового ответа) в главном таймлайне нет — он живёт в
 * thread.liveTimeline, — и проверка «цель существует» молча выкидывает
 * реакцию. Результат: в панели треда реакции на ответы невозможно ни
 * увидеть, ни корректно снять (снятие ищет myEventId в чипсах).
 *
 * Здесь множество допустимых целей расширено событиями треда (плюс корень:
 * он живёт в главном таймлайне и без того виден buildReactions, но дать
 * панели ПОЛНУЮ карту реакций одним вызовом дешевле, чем сливать две карты
 * в UI). Источник реакций по-прежнему главный таймлайн комнаты — другого
 * места, куда сервер кладёт m.reaction, в протоколе нет.
 */
export function buildThreadReactions(
  thread: Thread,
  me: string,
): Map<string, ReactionChip[]> {
  const roomEvents = thread.room.getLiveTimeline().getEvents();
  // Множество id, реакции на которые считаем «тредовыми»: все события
  // тредового таймлайна + корень. Остальные реакции комнаты сюда не
  // должны протечь — иначе панель показала бы чипсы чужих сообщений.
  const allowedTargets = new Set<string>();
  for (const ev of thread.liveTimeline.getEvents()) {
    const id = ev.getId();
    if (id) allowedTargets.add(id);
  }
  allowedTargets.add(thread.id);
  return aggregateReactions(roomEvents, me, allowedTargets);
}

/**
 * Ядро сбора реакций: обход СОБЫТИЙ-РЕАКЦИЙ в переданном списке.
 *
 * @param events — где ищем m.reaction (для комнаты и треда это один и тот
 *   же главный таймлайн: тредовый таймлайн реакций не содержит).
 * @param me — мой MXID (подсветка «моё» и myEventId для снятия).
 * @param allowedTargets — если задано, реакция учитывается только когда её
 *   цель входит в множество (режим треда). Если не задано — цель обязана
 *   найтись среди самих `events` (режим комнаты: реакция на невидимое/не
 *   загруженное событие отбрасывается, как и раньше).
 */
function aggregateReactions(
  events: MatrixEvent[],
  me: string,
  allowedTargets?: Set<string>,
): Map<string, ReactionChip[]> {
  const eventsById = new Map<string, MatrixEvent>();
  for (const ev of events) {
    const id = ev.getId();
    if (id) eventsById.set(id, ev);
  }

  /** targetId -> key -> { count, mine, myEventId, firstTs } */
  const byTarget = new Map<string, Map<string, { count: number; mine: boolean; myEventId?: string; firstTs: number }>>();

  for (const ev of events) {
    if (ev.getType() !== EventType.Reaction) continue;
    if (ev.isRedacted()) continue;
    const rel = ev.getRelation();
    if (rel?.rel_type !== "m.annotation") continue;
    const targetId = rel.event_id;
    const key = rel.key;
    if (!targetId || typeof key !== "string") continue;

    // В режиме треда фильтруем по множеству допустимых целей: цель может
    // жить в тредовом таймлайне и не находиться в eventsById — это норма,
    // а не сирота. Redact цели при этом проверить НЕ можем (события под
    // рукой нет) — осознанная цена: редкая реакция на удалённый тредовый
    // ответ покажется до перезагрузки, но обратная ошибка (скрывать все
    // тредовые реакции) была заметнее на каждом сообщении.
    if (allowedTargets && !allowedTargets.has(targetId)) continue;

    // Цель должна существовать и не быть redacted (режим комнаты).
    const target = eventsById.get(targetId);
    if (!allowedTargets && (!target || target.isRedacted())) continue;

    let keyMap = byTarget.get(targetId);
    if (!keyMap) {
      keyMap = new Map();
      byTarget.set(targetId, keyMap);
    }

    const sender = ev.getSender() ?? "";
    const ts = ev.getTs();
    const existing = keyMap.get(key);

    if (!existing) {
      keyMap.set(key, {
        count: 1,
        mine: sender === me,
        myEventId: sender === me ? ev.getId() : undefined,
        firstTs: ts,
      });
    } else {
      // Дубль от того же отправителя: перекрываем последним по времени.
      // Это защита от «поставил 👍, потом ещё раз 👍» — считаем один голос.
      if (sender === me) {
        existing.mine = true;
        existing.myEventId = ev.getId();
      }
      // Считаем только уникальных отправителей. Для этого храним Set
      // отправителей в отдельной Map — но для простоты MVP считаем
      // count как число СОБЫТИЙ реакции, а не людей. На стенде с 2-3
      // тестовыми пользователями разницы нет. TODO: дедуп по sender.
      existing.count++;
      existing.firstTs = Math.min(existing.firstTs, ts);
    }
  }

  // Превращаем в массив чипсов с сортировкой.
  const result = new Map<string, ReactionChip[]>();
  for (const [targetId, keyMap] of byTarget) {
    const chips: ReactionChip[] = [];
    for (const [key, data] of keyMap) {
      chips.push({
        key,
        count: data.count,
        mine: data.mine,
        myEventId: data.myEventId,
      });
    }
    chips.sort((a, b) => b.count - a.count);
    result.set(targetId, chips);
  }
  return result;
}
